import React, { useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { DatePicker } from 'syngenta-digital-cropwise-react-ui-kit';
import FilterDrawerShell from 'components/RecordsListView/FilterDrawerShell';
import { PlanFilters } from '../types';
import './PlanFilterDrawer.css';

const { RangePicker } = DatePicker;

export const PLAN_FILTER_DEFAULTS: PlanFilters = {
  creationDateRange: []
};

interface PlanFilterDrawerProps {
  visible: boolean;
  initialValues?: PlanFilters;
  onClose: () => void;
  onFiltersChange: (filters: PlanFilters) => void;
}

/**
 * Plans' filter drawer (FR-031). Built on the shared FilterDrawerShell, not
 * Applications/Work Orders' FilterDrawer, since Plans has no crop-zone/area/
 * source data for that filter's sections. The picked date range is applied
 * client-side by the caller, never sent to the API.
 */
const PlanFilterDrawer: React.FC<PlanFilterDrawerProps> = ({
  visible,
  initialValues,
  onClose,
  onFiltersChange
}) => {
  const adjustedInitialValues = useMemo(
    () => initialValues ?? PLAN_FILTER_DEFAULTS,
    [initialValues]
  );

  const formik = useFormik<PlanFilters>({
    initialValues: adjustedInitialValues,
    onSubmit: (values, actions) => {
      actions.setTouched({});
      onFiltersChange(values);
      actions.setSubmitting(false);
    }
  });

  const handleCancel = () => {
    formik.setValues(adjustedInitialValues);
    onClose();
  };

  useEffect(() => {
    if (!isEqual(formik.values, adjustedInitialValues)) {
      formik.setValues(adjustedInitialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustedInitialValues]);

  const isCleared = useMemo(
    () => isEqual(formik.values, PLAN_FILTER_DEFAULTS),
    [formik.values]
  );
  const isDirty = useMemo(
    () => !isEqual(formik.values, adjustedInitialValues),
    [formik.values, adjustedInitialValues]
  );
  const hasCreationDateFilter = useMemo(
    () => formik.values.creationDateRange?.length === 2,
    [formik.values.creationDateRange]
  );

  return (
    <FilterDrawerShell
      visible={visible}
      title="Filter"
      className="plan-filter-drawer"
      testId="plan-filter-drawer"
      applyDisabled={!isDirty}
      clearAllDisabled={isCleared}
      onSubmit={formik.handleSubmit}
      onCancel={handleCancel}
      onApply={() => {
        formik.handleSubmit();
        onClose();
      }}
      onClearAll={() => {
        Object.keys(formik.values).forEach(key => {
          formik.setFieldTouched(key, true);
        });
        formik.setValues(PLAN_FILTER_DEFAULTS);
      }}
    >
      <div className="flex flex-col px-4 pb-5">
        <div className="flex grow py-5">
          <h3 className="font-semibold">Creation Date</h3>
        </div>
        <div className="flex grow" id="plan-filter-date-range-picker-container">
          <RangePicker
            name="creationDateRange"
            onChange={(
              dates: [moment.Moment | null, moment.Moment | null] | null
            ) => {
              const [start, end] = dates ?? [];
              const normalized: PlanFilters['creationDateRange'] =
                start && end ? [start, end] : [];
              formik.setFieldValue('creationDateRange', normalized);
              formik.setFieldTouched('creationDateRange', true);
            }}
            value={formik.values.creationDateRange}
            className="w-full"
            format="MM/DD/YYYY"
          />
        </div>

        {hasCreationDateFilter && (
          <p
            className="text-xs text-gray-500 mt-2"
            data-testid="plan-filter-pagination-note"
          >
            Creation Date filters apply to the current page only and may not
            reflect all Plans across pagination.
          </p>
        )}
      </div>
    </FilterDrawerShell>
  );
};

export default PlanFilterDrawer;
